import {
  Card,
  CardBody,
  UncontrolledDropdown,
  DropdownToggle,
  DropdownMenu,
  DropdownItem,
} from "reactstrap";

import { SubjectDto } from "../../interfaces";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { useSelector } from "react-redux";
import { RoleEnum } from "../../enums";
import { AccountState } from "../../store/slices/accountSlice";

type TProps = SubjectDto & {
  onEditSubject?: (id: string) => void;
  onDeleteSubject?: (id: string) => void;
  onCopySubject?: (id: string) => void;
  onCutSubject?: (id: string) => void;
  onSyncSubject?: (id: string) => void;
  onToggleState?: (id: string) => void;
  isChangeable?: boolean;
  linkLabel?: string;
  link: string;
};

export const SubjectCard: React.FC<TProps> = ({
  id,
  isActive,
  homeImage,
  name,
  onEditSubject,
  onDeleteSubject,
  onCopySubject,
  onCutSubject,
  onSyncSubject,
  onToggleState,
  isChangeable = true,
  link,
  chaptersCount,
  lessonsCount,
  questionsCount,
  notesCount,
  flashCardsCount,
  lecturesCount,
  liveLecturesCount,
}) => {
  const { t, i18n } = useTranslation();
  const profile = useSelector(
    (state: { account: AccountState }) => state.account.userProfile,
  );
  const canManage =
    isChangeable && profile?.role.key === RoleEnum.SUPER_ADMIN;
  const [basePath, query = ""] = link.split("?");
  const tabHref = (segment: string) =>
    `${basePath}/${segment}${query ? `?${query}` : ""}`;
  const title = i18n.language === "ar" ? name?.ar : name?.en;
  const initials =
    (name?.en?.charAt(0) || "") +
    (name?.en?.split(" ").slice(-1).toString().charAt(0) || "");

  const stats = [
    {
      key: "chapters",
      count: chaptersCount || 0,
      label: "chapters",
      icon: "ri-book-open-line",
      href: tabHref("organizing"),
    },
    {
      key: "lessons",
      count: lessonsCount || 0,
      label: "lessons",
      icon: "ri-book-read-line",
      href: tabHref("organizing"),
    },
    {
      key: "questions",
      count: questionsCount || 0,
      label: "questions",
      icon: "ri-question-answer-line",
      href: tabHref("questions"),
    },
  ];

  const shortcuts = [
    {
      key: "notes",
      count: notesCount || 0,
      label: "notes",
      icon: "ri-file-text-line",
      href: tabHref("notes"),
    },
    {
      key: "cards",
      count: flashCardsCount || 0,
      label: "flashCards",
      icon: "ri-slideshow-2-line",
      href: tabHref("flash-cards-v2"),
    },
    {
      key: "lectures",
      count: lecturesCount || 0,
      label: "lectures",
      icon: "ri-video-line",
      href: tabHref("lectures"),
    },
    {
      key: "live",
      count: liveLecturesCount || 0,
      label: "liveLectures",
      icon: "ri-live-line",
      href: tabHref("live-lectures"),
    },
  ];

  return (
    <Card className="subject-overview-card h-100">
      <CardBody className="subject-card">
        <div className="subject-card__header">
          <div className="subject-card__avatar">
            {homeImage ? (
              <img src={homeImage} alt="" />
            ) : (
              <span>{initials}</span>
            )}
          </div>
          <div className="subject-card__identity">
            <Link to={link} className="subject-card__name" title={title}>
              {title}
            </Link>
            <span
              className={`subject-card__status ${
                isActive ? "is-active" : "is-inactive"
              }`}
            >
              <span className="subject-card__status-dot" />
              {isActive ? t("active") : t("inActive")}
            </span>
          </div>
          {canManage ? (
            <UncontrolledDropdown className="subject-card__menu">
              <DropdownToggle tag="button" className="subject-card__menu-btn">
                <i className="ri-more-fill" />
              </DropdownToggle>
              <DropdownMenu end>
                <DropdownItem onClick={() => onEditSubject?.(id)}>
                  <i className="ri-pencil-line me-2 align-bottom text-muted" />
                  {t("edit")}
                </DropdownItem>
                <DropdownItem onClick={() => onToggleState?.(id)}>
                  <i
                    className={`${
                      isActive ? "ri-forbid-line" : "ri-checkbox-circle-line"
                    } me-2 align-bottom text-muted`}
                  />
                  {isActive ? t("deactivate") : t("activate")}
                </DropdownItem>
                <DropdownItem onClick={() => onCopySubject?.(id)}>
                  <i className="ri-file-copy-line me-2 align-bottom text-muted" />
                  {t("copy")}
                </DropdownItem>
                <DropdownItem onClick={() => onCutSubject?.(id)}>
                  <i className="ri-scissors-line me-2 align-bottom text-muted" />
                  {t("cut")}
                </DropdownItem>
                <DropdownItem onClick={() => onSyncSubject?.(id)}>
                  <i className="ri-refresh-line me-2 align-bottom text-muted" />
                  {t("sync")}
                </DropdownItem>
                <DropdownItem onClick={() => onDeleteSubject?.(id)}>
                  <i className="ri-delete-bin-5-line me-2 align-bottom text-muted" />
                  {t("delete")}
                </DropdownItem>
              </DropdownMenu>
            </UncontrolledDropdown>
          ) : null}
        </div>

        <div className="subject-card__stats">
          {stats.map((item) => (
            <Link
              key={item.key}
              to={item.href}
              className="subject-card__stat"
              title={t(item.label)}
            >
              <i className={item.icon} />
              <span className="subject-card__stat-count">{item.count}</span>
              <span className="subject-card__stat-label">{t(item.label)}</span>
            </Link>
          ))}
        </div>

        <div className="subject-card__shortcuts">
          {shortcuts.map((item) => (
            <Link
              key={item.key}
              to={item.href}
              className="subject-card__shortcut"
              title={t(item.label)}
            >
              <i className={item.icon} />
              <span className="subject-card__shortcut-count">{item.count}</span>
              <span className="subject-card__shortcut-label">
                {t(item.label)}
              </span>
            </Link>
          ))}
        </div>

        <Link to={link} className="btn btn-primary subject-card__open">
          {t("openSubject")}
          <i
            className={
              i18n.language === "ar"
                ? "ri-arrow-left-line"
                : "ri-arrow-right-line"
            }
          />
        </Link>
      </CardBody>
    </Card>
  );
};
